import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, X, Check, ImagePlus, Sparkles } from 'lucide-react';
import { getAllCategories, getAllProducts, getSubcategories } from '../../data/catalog';
import {
  addCustomCategory,
  updateCustomCategory,
  removeCustomCategory,
  saveCategoryOverride,
  getCustomSubcategories,
  addSubcategory,
  updateSubcategory,
  removeSubcategory,
  slugify,
} from '../../utils/customProducts';
import { fileToDataUrl } from '../../utils/siteConfig';
import { uploadImage } from '../../utils/photoStatus';
import { useQueue } from '../../context/QueueContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Fallback local do prompt-base de categoria (o oficial vem de Integrações de IA → Padrão de imagem por IA).
const DEFAULT_CATEGORY_PROMPT =
  'Fotografia editorial premium para representar uma categoria de tecidos em catálogo de alto padrão. ' +
  'Categoria: {categoria}. Descrição: {descricao}. ' +
  'Criar uma composição visual elegante e coerente com os principais usos e características desta categoria. ' +
  'Os tecidos devem ser protagonistas da imagem, com textura, caimento e aparência claramente visíveis. ' +
  'Utilizar ambientação sofisticada e discreta, iluminação natural suave, fundo neutro e composição limpa. ' +
  'Não representar um único produto específico. Não adicionar pessoas, texto, logotipo ou marca d\'água. ' +
  'Manter consistência visual com as demais categorias do catálogo Huddersfield.';

function buildCategoryPrompt(c, useProducts, base) {
  let p = (base || DEFAULT_CATEGORY_PROMPT)
    .replaceAll('{categoria}', c.name)
    .replaceAll('{descricao}', c.description || c.tagline || '');
  if (useProducts) {
    const names = getAllProducts().filter((x) => x.categorySlug === c.slug).map((x) => x.name).slice(0, 6);
    if (names.length) p += `\nReferências de produtos reais desta categoria: ${names.join(', ')}.`;
  }
  return p;
}

// Otimiza a prévia aprovada (mesmo padrão das demais imagens do catálogo).
const inputCls =
  'mt-1 h-12 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/15';
const labelCls = 'block text-xs font-semibold uppercase tracking-wide text-muted-foreground';

export default function AdminCategories() {
  const { user } = useQueue();
  const [, setTick] = useState(0);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [subForm, setSubForm] = useState(null);
  const [confirmRemoveSub, setConfirmRemoveSub] = useState(null);
  const [aiGen, setAiGen] = useState(null);

  // Geração de imagem da categoria com IA: prompt visível → prévia → gerar novamente → aprovar.
  // Nada é publicado automaticamente; a imagem só vira imagem da categoria após APROVAR E SALVAR.
  const startAiGen = async (c) => {
    let base = DEFAULT_CATEGORY_PROMPT;
    try {
      const res = await fetch(`${API}/api/ai/settings`, { headers: { 'X-Admin-Role': user?.role || '' } });
      if (res.ok) {
        const s = await res.json();
        base = s.promptCategoryImage || base;
      }
    } catch {}
    setAiGen({ slug: c.slug, name: c.name, base, useProducts: true, prompt: buildCategoryPrompt(c, true, base), preview: null, loading: false, saving: false, error: '' });
  };

  const generateAi = async () => {
    setAiGen((g) => ({ ...g, loading: true, error: '', preview: null }));
    try {
      const res = await fetch(`${API}/api/ai/generate-category-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Role': user?.role || '' },
        body: JSON.stringify({ prompt: aiGen.prompt }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Falha ao gerar imagem.');
      setAiGen((g) => ({ ...g, loading: false, preview: d.image }));
    } catch (e) {
      setAiGen((g) => ({ ...g, loading: false, error: e.message }));
    }
  };

  const approveAi = async () => {
    setAiGen((g) => ({ ...g, saving: true, error: '' }));
    try {
      // A imagem gerada já está no armazenamento durável do servidor (URL estável) — o banco guarda só a referência.
      await saveCategoryOverride(aiGen.slug, { image: aiGen.preview });
      setAiGen(null);
      notify('Imagem da categoria gerada com IA e aprovada.');
    } catch (e) {
      setAiGen((g) => ({ ...g, saving: false, error: e.message || 'Falha ao salvar a imagem.' }));
    }
  };

  const aiBlock = (c) => (
    <>
      <button
        type="button"
        onClick={() => (aiGen?.slug === c.slug ? setAiGen(null) : startAiGen(c))}
        data-testid={`cat-ai-generate-${c.slug}`}
        className="mt-2 flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-navy/30 text-xs font-semibold text-navy transition-colors hover:bg-navy-light"
      >
        <Sparkles className="h-4 w-4" /> GERAR IMAGEM DA CATEGORIA COM IA
      </button>
      {aiGen && aiGen.slug === c.slug && (
        <div className="mt-2 rounded-xl border border-navy/15 bg-white p-3" data-testid="cat-ai-panel">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Prompt que será usado — confira antes de gerar</p>
          <textarea
            value={aiGen.prompt}
            onChange={(e) => setAiGen({ ...aiGen, prompt: e.target.value })}
            rows={6}
            data-testid="cat-ai-prompt"
            className="mt-1 w-full rounded-lg border border-input bg-white px-3 py-2 text-xs outline-none focus:border-navy"
          />
          <label className="mt-2 flex items-center gap-2 text-xs text-foreground/80">
            <input
              type="checkbox"
              checked={aiGen.useProducts}
              onChange={(e) => setAiGen((g) => ({ ...g, useProducts: e.target.checked, prompt: buildCategoryPrompt(c, e.target.checked, g.base) }))}
              data-testid="cat-ai-use-products"
              className="h-4 w-4"
            />
            Usar produtos reais da categoria como referência
          </label>
          {aiGen.error && (
            <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700" data-testid="cat-ai-error">{aiGen.error}</p>
          )}
          {aiGen.preview && (
            <img src={aiGen.preview} alt={`Prévia da imagem da categoria ${aiGen.name}`} className="mt-3 w-full rounded-lg" data-testid="cat-ai-preview" />
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {!aiGen.preview ? (
              <button
                type="button"
                onClick={generateAi}
                disabled={aiGen.loading}
                data-testid="cat-ai-generate-preview"
                className="flex h-11 items-center gap-2 rounded-xl bg-navy px-5 text-xs font-bold text-white hover:bg-navy-hover disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" /> {aiGen.loading ? 'GERANDO…' : 'GERAR PRÉVIA'}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={generateAi}
                  disabled={aiGen.loading}
                  data-testid="cat-ai-regenerate"
                  className="flex h-11 items-center gap-2 rounded-xl border border-navy/25 px-5 text-xs font-bold text-navy hover:bg-navy-light disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4" /> {aiGen.loading ? 'GERANDO…' : 'GERAR NOVAMENTE'}
                </button>
                <button
                  type="button"
                  onClick={approveAi}
                  disabled={aiGen.saving}
                  data-testid="cat-ai-approve"
                  className="flex h-11 items-center gap-2 rounded-xl bg-emerald-600 px-5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  <Check className="h-4 w-4" /> {aiGen.saving ? 'SALVANDO…' : 'APROVAR E SALVAR'}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setAiGen(null)}
              data-testid="cat-ai-cancel"
              className="flex h-11 items-center gap-2 rounded-xl border border-navy/20 px-5 text-xs font-semibold text-navy hover:bg-navy-light"
            >
              <X className="h-4 w-4" /> CANCELAR
            </button>
          </div>
          <p className="mt-2 text-[10px] leading-relaxed text-muted-foreground">
            A imagem só entra no ar após a sua aprovação. Ela é otimizada e enviada ao armazenamento
            durável do servidor; o banco guarda apenas a URL/metadados.
          </p>
        </div>
      )}
    </>
  );

  if (user?.role !== 'admin') return <Navigate to="/admin/dashboard" replace />;

  const allCats = getAllCategories();
  const categoryProducts = (slug) => getAllProducts().filter((p) => p.categorySlug === slug);
  const categorySubcategories = (slug) => getSubcategories(slug);

  const notify = (msg) => {
    setFlash(msg);
    setTick((t) => t + 1);
    setTimeout(() => setFlash(''), 3000);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      // Imagem de categoria: otimizada no navegador e enviada ao armazenamento durável;
      // o cadastro guarda apenas a URL (continua sendo imagem de CATEGORIA, sem misturar com produto).
      const dataUrl = await fileToDataUrl(file, 800);
      const url = await uploadImage(dataUrl, 'categorias');
      setForm((f) => ({ ...f, image: url }));
    } catch (err) {
      setError(err.message || 'Falha ao enviar a imagem.');
    }
    e.target.value = '';
  };

  const save = async () => {
    setError('');
    const name = form.name.trim();
    if (!name) {
      setError('Informe o nome da categoria.');
      return;
    }
    const payload = {
      name,
      tagline: form.tagline.trim(),
      description: form.description.trim(),
      ...(form.image ? { image: form.image } : {}),
    };
    try {
      if (form.slug) {
        await updateCustomCategory(form.slug, payload);
        notify('Categoria atualizada.');
      } else {
        const slug = slugify(name);
        if (getAllCategories().some((c) => c.slug === slug)) {
          setError('Já existe uma categoria com este nome.');
          return;
        }
        await addCustomCategory({ slug, ...payload, custom: true });
        notify('Categoria cadastrada.');
      }
      setForm(null);
    } catch (e) {
      setError(e.message || 'Não foi possível salvar a categoria.');
    }
  };

  const remove = async (c) => {
    const products = categoryProducts(c.slug);
    const mode = confirmRemove?.mode || (products.length ? 'move' : 'cascade');
    const targetSlug = confirmRemove?.targetSlug || '';
    if (products.length && mode === 'move' && !targetSlug) {
      setError('Escolha a categoria que receberá os produtos.');
      return;
    }
    try {
      const result = await removeCustomCategory(c.slug, { mode, targetCategorySlug: targetSlug });
      setConfirmRemove(null);
      if (mode === 'move') {
        notify(`Categoria removida. ${result.movedProducts} produto(s) movidos e ${result.deletedSubcategories} subcategoria(s) removida(s).`);
      } else {
        notify(`Categoria removida junto com ${result.deletedProducts} produto(s) e ${result.deletedSubcategories} subcategoria(s).`);
      }
    } catch (e) {
      setError(e.message || 'Não foi possível remover a categoria.');
    }
  };

  const saveSub = async () => {
    setError('');
    const name = subForm.name.trim();
    if (!name) {
      setError('Informe o nome da subcategoria.');
      return;
    }
    try {
      if (subForm.slug) {
        await updateSubcategory(subForm.slug, { name });
        notify('Subcategoria atualizada.');
      } else {
        const slug = slugify(name);
        if (getCustomSubcategories().some((s) => s.slug === slug)) {
          setError('Já existe uma subcategoria com este nome.');
          return;
        }
        await addSubcategory({ slug, categorySlug: subForm.categorySlug, name });
        notify('Subcategoria cadastrada.');
      }
      setSubForm(null);
    } catch (e) {
      setError(e.message || 'Não foi possível salvar a subcategoria.');
    }
  };

  const removeSub = async (s) => {
    if (getAllProducts().some((p) => p.subcategorySlug === s.slug)) {
      setConfirmRemoveSub(null);
      setError(`A subcategoria “${s.name}” possui produtos vinculados e não pode ser removida. Edite os produtos primeiro.`);
      return;
    }
    try {
      await removeSubcategory(s.slug);
      setConfirmRemoveSub(null);
      notify('Subcategoria removida.');
    } catch (e) {
      setConfirmRemoveSub(null);
      setError(e.message || 'Não foi possível remover a subcategoria.');
    }
  };

  const subSection = (c) => {
    const subs = getSubcategories(c.slug);
    return (
      <div className="mt-3 border-t border-navy/10 pt-3" data-testid={`sub-list-${c.slug}`}>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Subcategorias</p>
        {subs.length === 0 && <p className="mt-1 text-xs text-muted-foreground" data-testid={`sub-empty-${c.slug}`}>Nenhuma subcategoria.</p>}
        <div className="mt-2 space-y-2">
          {subs.map((s) => (
            <div key={s.slug} className="flex items-center gap-2" data-testid={`sub-row-${s.slug}`}>
              {confirmRemoveSub === s.slug ? (
                <>
                  <p className="min-w-0 flex-1 text-xs font-medium text-red-700">Remover “{s.name}”? Esta ação não pode ser desfeita.</p>
                  <button type="button" onClick={() => setConfirmRemoveSub(null)} data-testid={`sub-remove-cancel-${s.slug}`} className="rounded-full border border-navy/20 px-3 py-2 text-[11px] font-semibold text-navy">CANCELAR</button>
                  <button type="button" onClick={() => removeSub(s)} data-testid={`sub-remove-do-${s.slug}`} className="rounded-full bg-red-600 px-3 py-2 text-[11px] font-semibold text-white">REMOVER</button>
                </>
              ) : (
                <>
                  <p className="min-w-0 flex-1 truncate text-sm">{s.name}</p>
                  <button type="button" onClick={() => { setSubForm({ categorySlug: c.slug, slug: s.slug, name: s.name }); setError(''); }} data-testid={`sub-edit-${s.slug}`} aria-label={`Editar ${s.name}`} className="rounded-lg border border-navy/20 p-2 text-navy hover:bg-navy-light">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => { setConfirmRemoveSub(s.slug); setError(''); }} data-testid={`sub-remove-${s.slug}`} aria-label={`Remover ${s.name}`} className="rounded-lg border border-red-300 p-2 text-red-700 hover:bg-red-50">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
        {subForm && subForm.categorySlug === c.slug ? (
          <div className="mt-2 flex items-center gap-2" data-testid="sub-form">
            <input
              value={subForm.name}
              onChange={(e) => setSubForm({ ...subForm, name: e.target.value })}
              placeholder="Ex.: Microfibra"
              aria-label="Nome da subcategoria"
              data-testid="sub-name-input"
              className="h-10 min-w-0 flex-1 rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-navy"
            />
            <button type="button" onClick={saveSub} data-testid="sub-save-button" aria-label="Salvar subcategoria" className="rounded-lg bg-navy p-2.5 text-white hover:bg-navy-hover">
              <Check className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => { setSubForm(null); setError(''); }} data-testid="sub-cancel-button" aria-label="Cancelar" className="rounded-lg border border-navy/20 p-2.5 text-navy hover:bg-navy-light">
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { setSubForm({ categorySlug: c.slug, slug: null, name: '' }); setError(''); }}
            data-testid={`sub-new-${c.slug}`}
            className="mt-2 flex items-center gap-1.5 rounded-lg border border-dashed border-navy/25 px-3 py-2 text-[11px] font-semibold text-navy hover:bg-navy-light"
          >
            <Plus className="h-3.5 w-3.5" /> ADICIONAR SUBCATEGORIA
          </button>
        )}
      </div>
    );
  };

  return (
    <div data-testid="admin-categories-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl text-navy">Categorias</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            As categorias aparecem na navegação pública (ordem alfabética, “Todos os Tecidos” por último) e no
            cadastro de produtos. A vitrine da Home é controlada em Personalização do Site.
          </p>
        </div>
        {!form && (
          <button
            type="button"
            onClick={() => { setForm({ slug: null, name: '', tagline: '', description: '', image: null }); setError(''); }}
            data-testid="cat-new-button"
            className="flex items-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white hover:bg-navy-hover"
          >
            <Plus className="h-4 w-4" /> NOVA CATEGORIA
          </button>
        )}
      </div>

      {flash && (
        <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800" data-testid="cat-flash">{flash}</p>
      )}
      {error && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert" data-testid="cat-form-error">{error}</p>
      )}

      {form && (
        <div className="mt-4 rounded-2xl border border-navy/15 bg-white p-5" data-testid="cat-form">
          <p className="text-sm font-semibold text-navy">{form.slug ? 'Editar categoria' : 'Nova categoria'}</p>
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="cat-name" className={labelCls}>Nome</label>
              <input id="cat-name" data-testid="cat-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Ex.: Tricoline" />
            </div>
            <div>
              <label htmlFor="cat-tagline" className={labelCls}>Pequeno texto (tagline)</label>
              <input id="cat-tagline" data-testid="cat-tagline-input" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={inputCls} placeholder="Ex.: Estampas para artesanato" />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="cat-description" className={labelCls}>Descrição</label>
              <textarea id="cat-description" data-testid="cat-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1 w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none focus:border-navy" />
            </div>
            <div>
              <p className={labelCls}>Imagem (otimizada automaticamente)</p>
              {form.image && <img src={form.image} alt="" className="mt-2 h-24 w-32 rounded-lg object-cover" data-testid="cat-image-preview" />}
              <label htmlFor="cat-image" data-testid="cat-image-label" className="mt-2 flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-dashed border-navy/25 px-4 py-3 text-xs font-semibold text-navy hover:bg-navy-light">
                <ImagePlus className="h-4 w-4" /> {form.image ? 'SUBSTITUIR IMAGEM' : 'ENVIAR IMAGEM'}
              </label>
              <input id="cat-image" data-testid="cat-image-input" type="file" accept="image/*" className="sr-only" onChange={upload} />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:flex">
            <button type="button" onClick={() => { setForm(null); setError(''); }} data-testid="cat-cancel-button" className="flex items-center justify-center gap-2 rounded-full border border-navy/20 px-5 py-3 text-sm font-semibold text-navy hover:bg-navy-light">
              <X className="h-4 w-4" /> CANCELAR
            </button>
            <button type="button" onClick={save} data-testid="cat-save-button" className="flex items-center justify-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white hover:bg-navy-hover">
              <Check className="h-4 w-4" /> SALVAR CATEGORIA
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" data-testid="cat-list">
        {allCats.map((c) => {
          const linkedProducts = categoryProducts(c.slug);
          const linkedSubs = categorySubcategories(c.slug);
          const removing = confirmRemove?.slug === c.slug;
          const removeMode = confirmRemove?.mode || 'cascade';
          const hasLinks = linkedProducts.length > 0 || linkedSubs.length > 0;
          return (
            <div key={c.slug} className="overflow-hidden rounded-2xl border border-[#CBD5E1] bg-white shadow-sm transition-all duration-200 hover:border-navy hover:shadow-md" data-testid={`cat-row-${c.slug}`}>
              <div className="relative">
                {c.image ? (
                  <img src={c.image} alt={c.name} className="mx-auto mt-4 h-36 w-28 rounded-xl border border-slate-200 object-cover shadow-sm" loading="lazy" />
                ) : (
                  <div className="mx-auto mt-4 flex h-36 w-28 items-center justify-center rounded-xl border border-slate-200 bg-navy-light shadow-sm" data-testid={`cat-no-image-${c.slug}`}>
                    <span className="font-serif text-4xl text-navy/25">{c.name.charAt(0)}</span>
                  </div>
                )}
              </div>
              <div className="p-3">
                <p className="truncate font-medium leading-tight">{c.name}</p>
                <p className="truncate text-xs text-muted-foreground">{c.tagline || c.description}</p>
                {removing ? (
                  <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3" data-testid={`cat-remove-confirm-${c.slug}`}>
                    <p className="text-xs font-medium text-red-700">Remover “{c.name}”? Esta ação não pode ser desfeita.</p>
                    {hasLinks && (
                      <p className="mt-2 text-xs leading-relaxed text-red-700" data-testid={`cat-remove-links-${c.slug}`}>
                        Vínculos encontrados: {linkedProducts.length} produto(s) e {linkedSubs.length} subcategoria(s).
                      </p>
                    )}
                    {linkedProducts.length > 0 && allCats.length > 1 && (
                      <div className="mt-3 space-y-2" data-testid={`cat-remove-options-${c.slug}`}>
                        <label className="flex items-start gap-2 text-xs font-semibold text-navy">
                          <input
                            type="radio"
                            name={`cat-remove-mode-${c.slug}`}
                            checked={removeMode === 'move'}
                            onChange={() => setConfirmRemove({ ...confirmRemove, mode: 'move' })}
                            data-testid={`cat-remove-mode-move-${c.slug}`}
                            className="mt-0.5 h-4 w-4"
                          />
                          Mover os produtos para outra categoria e remover as subcategorias.
                        </label>
                        {removeMode === 'move' && (
                          <select
                            value={confirmRemove?.targetSlug || ''}
                            onChange={(e) => setConfirmRemove({ ...confirmRemove, targetSlug: e.target.value })}
                            aria-label="Categoria que receberá os produtos"
                            data-testid={`cat-remove-target-${c.slug}`}
                            className="h-10 w-full rounded-lg border border-input bg-white px-3 text-xs outline-none focus:border-navy"
                          >
                            {allCats.filter((x) => x.slug !== c.slug).map((x) => (
                              <option key={x.slug} value={x.slug}>{x.name}</option>
                            ))}
                          </select>
                        )}
                        <label className="flex items-start gap-2 text-xs font-semibold text-red-700">
                          <input
                            type="radio"
                            name={`cat-remove-mode-${c.slug}`}
                            checked={removeMode === 'cascade'}
                            onChange={() => setConfirmRemove({ ...confirmRemove, mode: 'cascade' })}
                            data-testid={`cat-remove-mode-cascade-${c.slug}`}
                            className="mt-0.5 h-4 w-4"
                          />
                          Excluir a categoria junto com os produtos e subcategorias vinculados.
                        </label>
                      </div>
                    )}
                    {linkedProducts.length > 0 && allCats.length === 1 && (
                      <p className="mt-2 text-xs font-medium text-red-700" data-testid={`cat-remove-single-${c.slug}`}>
                        Esta é a única categoria; os produtos vinculados também serão excluídos.
                      </p>
                    )}
                    {linkedProducts.length === 0 && linkedSubs.length > 0 && (
                      <p className="mt-2 text-xs font-medium text-red-700" data-testid={`cat-remove-sub-warning-${c.slug}`}>
                        As subcategorias vinculadas serão removidas junto.
                      </p>
                    )}
                    <div className="mt-3 flex gap-2">
                      <button type="button" onClick={() => setConfirmRemove(null)} data-testid={`cat-remove-cancel-${c.slug}`} className="flex-1 rounded-full border border-navy/20 px-4 py-2.5 text-xs font-semibold text-navy">CANCELAR</button>
                      <button type="button" onClick={() => remove(c)} data-testid={`cat-remove-do-${c.slug}`} className="flex-1 rounded-full bg-red-600 px-4 py-2.5 text-xs font-semibold text-white">
                        {removeMode === 'move' && linkedProducts.length > 0 ? 'MOVER E REMOVER' : 'REMOVER'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link to={`/admin/produtos/novo?categoria=${c.slug}`} data-testid={`cat-add-product-${c.slug}`} className="flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-navy px-4 text-xs font-semibold text-white hover:bg-navy-hover">
                      <Plus className="h-4 w-4" /> ADICIONAR PRODUTO
                    </Link>
                    <button type="button" onClick={() => { setForm({ slug: c.slug, name: c.name, tagline: c.tagline || '', description: c.description || '', image: c.image || null }); setError(''); }} data-testid={`cat-edit-${c.slug}`} className="flex h-10 items-center gap-2 rounded-xl border border-navy/20 px-4 text-xs font-semibold text-navy hover:bg-navy-light">
                      <Pencil className="h-4 w-4" /> EDITAR
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const canMove = linkedProducts.length > 0 && allCats.length > 1;
                        const firstTarget = canMove ? allCats.find((x) => x.slug !== c.slug)?.slug || '' : '';
                        setConfirmRemove({ slug: c.slug, mode: canMove ? 'move' : 'cascade', targetSlug: firstTarget });
                        setError('');
                      }}
                      data-testid={`cat-remove-${c.slug}`}
                      className="flex h-10 items-center gap-2 rounded-xl border border-red-300 px-4 text-xs font-semibold text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" /> REMOVER
                    </button>
                  </div>
                )}
                {aiBlock(c)}
                {subSection(c)}
              </div>
            </div>
          );
        })}
        {allCats.length === 0 && (
          <p className="rounded-2xl border border-dashed border-navy/20 p-6 text-center text-sm text-muted-foreground" data-testid="cat-empty-note">
            Nenhuma categoria cadastrada.
          </p>
        )}
      </div>
    </div>
  );
}
